export type VueAppInterop<ScopeName> = {
    [Property in keyof ScopeName]?: {
        isVueAppInitialized: boolean;
        selfDestroy: () => void;
        [key: string]: any;
    };
}

type IsAppAvailableExternally = boolean;

/*
    Реестр интеропа читается на каждом вызове, а не снимается один раз при
    загрузке модуля. Прежний снимок (`let globalVueAppInterop = self.vueAppInterop`)
    вычислялся при импорте: если объекта тогда не было, а позже его создавал
    кто-то другой — а витрина КРМ создаёт его руками в `ExpressCollectionsApp` и
    `PrematchApp` тем же `if (!window.vueAppInterop)`, — хук по-прежнему видел
    своё устаревшее `undefined`, уходил в ветку создания и **перезатирал** реестр
    вместе с уже зарегистрированными в нём скоупами. Сейчас существующий объект
    не заменяется никогда.

    Второе: регистрация больше не зависит от того, первый это вызов или нет.
    Раньше ветка «объекта ещё нет» создавала реестр и молча **не** записывала
    scope, то есть самый первый потребитель терялся, а `isAvailable` всё равно
    возвращал `true`. Теперь запись делается всегда, когда имя передано; вызов
    без имени (в `Krm.UI/main.ts` он ровно для того и стоит, чтобы реестр
    появился) ведёт себя как прежде — создаёт объект и ничего не пишет.
*/
/*
    Дженерик `T` — декоративный: `data` он не типизирует, поэтому расхождение
    имён между объявленным скоупом и содержимым объекта тайпчек не ловит (так и
    случилось с `isVueAppInitialized` / `isAppInitialized` в акциях). Сузить его
    мешает бесаргументный вызов-инициализатор в `Krm.UI/main.ts`: он передаёт
    пустой объект, и строгая форма `data` его сломает. Сначала надо решить
    судьбу этого вызова, потом закрывать контракт.
*/
export function useVueAppExternally<T = any>(scopeName = '', data = {}, selfDestroyUsefullCallback?: (...args: unknown[]) => any): IsAppAvailableExternally {
    /*
        Реестр внутри функции типизирован свободно — как и было до правки, где
        он приезжал из `self.vueAppInterop` через `@ts-ignore` и потому был
        `any`. Строгий `VueAppInterop<T>` здесь не годится: это mapped-тип по
        `keyof T`, и индексировать его произвольным `scopeName: string` нельзя.
        Экспортируемый `VueAppInterop` остаётся для потребителей реестра.
    */
    //@ts-ignore -- глобального объявления `self.vueAppInterop` в этом слое нет
    const interop: Record<string, any> = (self.vueAppInterop ??= {});

    if (scopeName) {
        interop[scopeName] = data;
        interop[scopeName].selfDestroy = (...args: unknown[]) => {
            if (selfDestroyUsefullCallback) selfDestroyUsefullCallback(...args);
            delete interop[scopeName];
        };
    }

    return true;
}
